import React, { useRef, useState, useEffect } from "react";
import jsyaml from "js-yaml";

export default function TractumSvgEditor({ initialYaml, onClose }) {
  const svgRef = useRef(null);

  // viewport
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [panStart, setPanStart] = useState(null);

  // ontology objects (nodes include areas & edges in same array)
  const [nodes, setNodes] = useState([]); // node | edge | area
  const [selectedId, setSelectedId] = useState(null);

  // editor state
  const [yamlText, setYamlText] = useState(initialYaml || "");
  const [message, setMessage] = useState("");

  // copy/paste states
  const [copiedNode, setCopiedNode] = useState(null);
  const [awaitingPaste, setAwaitingPaste] = useState(false);

  // dragging states
  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // sample yaml
  const SAMPLE_YAML = `tractum-map:
  nodes:
    - id: "desk01"
      label: "Desk A"
      x: 100
      y: 100
      ports:
        - id: "top"
          offsetX: 25
          offsetY: 0
        - id: "bottom"
          offsetX: 25
          offsetY: 50
      style:
        color: "#52ff7a"
      behavior:
        onClick: "showTooltip:Desk A status"

    - id: "desk02"
      label: "Desk B"
      x: 300
      y: 100
      ports:
        - id: "top"
          offsetX: 25
          offsetY: 0
        - id: "bottom"
          offsetX: 25
          offsetY: 50
      style:
        color: "#ff5252"
      behavior:
        onClick: "showTooltip:Desk B status"

    - id: "sensor01"
      label: "Occupancy Sensor"
      x: 200
      y: 250
      ports:
        - id: "left"
          offsetX: 0
          offsetY: 15
        - id: "right"
          offsetX: 50
          offsetY: 15
      style:
        color: "#ffd54f"
      behavior:
        onClick: "openPanel:sensor-panel"

  edges:
    - id: "e01"
      type: "edge"
      from: "desk01"
      to: "sensor01"
      geometry:
        x1: 100
        y1: 100
        x2: 200
        y2: 250
      style:
        color: "#33ccff"

    - id: "e02"
      type: "edge"
      from: "desk02"
      to: "sensor01"
      geometry:
        x1: 300
        y1: 100
        x2: 200
        y2: 250
      style:
        color: "#33ccff"

  areas:
    - id: "office_area"
      label: "Open Office"
      geometry:
        x: 50
        y: 50
        width: 350
        height: 300
      style:
        color: "#3399ff33"

`;



  // -------------------------
  // YAML <-> state helpers
  // -------------------------
  const loadYamlToState = (text) => {
    try {
      const parsed = jsyaml.load(text);
      if (!parsed || !parsed["tractum-map"]) {
        setMessage("YAML parsed but missing `tractum-map` root");
        return;
      }

      const nodesFromYaml = parsed["tractum-map"].nodes || [];
      const edgesFromYaml = parsed["tractum-map"].edges || [];
      const areasFromYaml = parsed["tractum-map"].areas || [];

      const normNodes = nodesFromYaml.map((n) => ({
        id: n.id,
        type: "node",
        position: n.position || { x: 0, y: 0 },
        dimension: n.dimension || { r: 15 },
        ports: n.ports || [], // ports inside node
        semantic: n.semantic || {},
        relations: n.relations || {},
        behavior: n.behavior || {},
        style: n.style || { color: "#999" },
        state: n.state || { selected: false, active: false },
      }));

      const normEdges = edgesFromYaml.map((e) => ({
        id: e.id,
        type: "edge",
        geometry: e.geometry || { x1: 0, y1: 0, x2: 0, y2: 0 },
        meta: e.meta || {}, // meta.from, meta.to, optional ports
        style: e.style || { color: "#aaa" },
        state: e.state || { selected: false },
      }));

      const normAreas = areasFromYaml.map((a) => ({
        id: a.id,
        type: "area",
        geometry: a.geometry,
        semantic: a.semantic || {},
        style: a.style || { color: "#8888" },
        state: a.state || { selected: false, active: false },
      }));

      setNodes([...normNodes, ...normEdges, ...normAreas]);
      setMessage("Loaded YAML into canvas");
    } catch (err) {
      setMessage("YAML parse error: " + err.message);
    }
  };

  // compute edge geometry from node positions + ports (returns [x1,y1,x2,y2])
  const computeEdgeGeometry = (edge, currentNodes) => {
    // find nodes
    const fromId = edge.meta?.from;
    const toId = edge.meta?.to;

    const fromNode = currentNodes.find((n) => n.id === fromId && n.type === "node");
    const toNode = currentNodes.find((n) => n.id === toId && n.type === "node");

    // helper: pick port by id or fallback side-based or center
    const choosePortPos = (node, portIdPreferred, preferSide) => {
      if (!node) return null;
      // find exact port
      const port = node.ports?.find((p) => p.id === portIdPreferred) || node.ports?.find((p) => p.side === preferSide) || node.ports?.[0];
      if (!port) {
        return { x: node.position.x, y: node.position.y };
      }
      const pos = computePortWorldPosition(node, port);
      return pos;
    };

    // compute start
    let x1 = edge.geometry?.x1 ?? 0;
    let y1 = edge.geometry?.y1 ?? 0;
    let x2 = edge.geometry?.x2 ?? 0;
    let y2 = edge.geometry?.y2 ?? 0;

    if (fromNode) {
      const pos = choosePortPos(fromNode, edge.meta?.fromPort, "right") || {};
      x1 = pos.x ?? x1;
      y1 = pos.y ?? y1;
    } else if (edge.geometry?.x1 !== undefined) {
      x1 = edge.geometry.x1;
      y1 = edge.geometry.y1;
    }

    if (toNode) {
      const pos2 = choosePortPos(toNode, edge.meta?.toPort, "left") || {};
      x2 = pos2.x ?? x2;
      y2 = pos2.y ?? y2;
    } else if (edge.geometry?.x2 !== undefined) {
      x2 = edge.geometry.x2;
      y2 = edge.geometry.y2;
    }

    return { x1, y1, x2, y2 };
  };

  const exportStateToYaml = () => {
    // nodes out: only node-type as YAML nodes
    const nodesOut = nodes
      .filter((n) => n.type === "node")
      .map((n) => ({
        id: n.id,
        position: n.position,
        dimension: n.dimension,
        ports: n.ports,
        semantic: n.semantic,
        relations: n.relations,
        behavior: n.behavior,
        style: n.style,
        state: n.state,
      }));

    // edges out: include computed geometry if from/to present
    const edgesOut = nodes
      .filter((n) => n.type === "edge")
      .map((e) => {
        const geo = computeEdgeGeometry(e, nodes);
        return {
          id: e.id,
          type: "edge",
          geometry: geo,
          meta: e.meta,
          style: e.style,
          state: e.state,
        };
      });

    const areasOut = nodes
      .filter((n) => n.type === "area")
      .map((a) => ({
        id: a.id,
        geometry: a.geometry,
        semantic: a.semantic,
        style: a.style,
        state: a.state,
      }));

    const yamlObj = { "tractum-map": { nodes: nodesOut, edges: edgesOut, areas: areasOut } };
    const text = jsyaml.dump(yamlObj);
    setYamlText(text);
    setMessage("Exported current canvas to YAML");
    return text;
  };

  // -------------------------
  // init
  // -------------------------
  useEffect(() => {
    const toLoad = initialYaml || SAMPLE_YAML;
    setYamlText(toLoad);
    loadYamlToState(toLoad);
  }, []); // eslint-disable-line

  // -------------------------
  // coordinate helpers
  // -------------------------
  const getSvgCoords = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    // convert screen -> world coordinates (account for pan + zoom)
    const x = (e.clientX - rect.left - offset.x) / zoom;
    const y = (e.clientY - rect.top - offset.y) / zoom;
    return { x, y };
  };

  const computePortWorldPosition = (node, port) => {
    // node.position assumed center-based
    const nx = node.position.x;
    const ny = node.position.y;
    const ox = port.offsetX ?? 0;
    const oy = port.offsetY ?? 0;
    return { x: nx + ox, y: ny + oy };
  };

  // -------------------------
  // Panning (background) and paste-on-click
  // -------------------------
  const handleBackgroundMouseDown = (e) => {
    // If awaiting paste, place the copied node where user clicked
    if (awaitingPaste && copiedNode) {
      const { x, y } = getSvgCoords(e);
      const newId = copiedNode.id + "-copy-" + Date.now();
      if (copiedNode.type === "node" || copiedNode.type === undefined) {
        const clone = {
          ...JSON.parse(JSON.stringify(copiedNode)),
          id: newId,
          type: "node",
          position: { x, y },
          state: { selected: false, active: false },
        };
        setNodes((prev) => {
          const out = [...prev, clone];
          setTimeout(exportStateToYaml, 0);
          return out;
        });
      } else if (copiedNode.type === "area") {
        const clone = {
          ...JSON.parse(JSON.stringify(copiedNode)),
          id: newId,
          type: "area",
          geometry: { ...copiedNode.geometry, x, y },
          state: { selected: false, active: false },
        };
        setNodes((prev) => {
          const out = [...prev, clone];
          setTimeout(exportStateToYaml, 0);
          return out;
        });
      }
      setAwaitingPaste(false);
      setCopiedNode(null);
      setMessage("Pasted node to canvas");
      return;
    }

    // otherwise start panning if clicked on empty canvas
    if (e.target === svgRef.current || e.target.dataset.canvas) {
      setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
      setSelectedId(null);
    }
  };

  const handleBackgroundMouseMove = (e) => {
    if (panStart) {
      setOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    }
  };

  const handleBackgroundMouseUp = () => {
    setPanStart(null);
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleBackgroundMouseMove);
    window.addEventListener("mouseup", handleBackgroundMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleBackgroundMouseMove);
      window.removeEventListener("mouseup", handleBackgroundMouseUp);
    };
  }, [panStart]);

  // -------------------------
  // Dragging nodes/areas
  // -------------------------
  const startDrag = (item, e) => {
    e.stopPropagation();
    // compute drag offset in world coords
    const coords = getSvgCoords(e);
    if (item.type === "node") {
      setDragOffset({ x: coords.x - item.position.x, y: coords.y - item.position.y });
    } else if (item.type === "area") {
      setDragOffset({ x: coords.x - item.geometry.x, y: coords.y - item.geometry.y });
    } else {
      setDragOffset({ x: 0, y: 0 });
    }
    setDraggingId(item.id);
    setSelectedId(item.id);
  };

  const handleDragMove = (e) => {
    if (!draggingId) return;
    const coords = getSvgCoords(e);
    setNodes((prev) =>
      prev.map((n) => {
        if (n.id !== draggingId) return n;
        if (n.type === "node") {
          return { ...n, position: { x: coords.x - dragOffset.x, y: coords.y - dragOffset.y } };
        }
        if (n.type === "area") {
          return { ...n, geometry: { ...n.geometry, x: coords.x - dragOffset.x, y: coords.y - dragOffset.y } };
        }
        return n;
      })
    );
  };

  const stopDrag = () => {
    if (draggingId) {
      exportStateToYaml(); // persist positions + ports
    }
    setDraggingId(null);
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleDragMove);
    window.addEventListener("mouseup", stopDrag);
    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", stopDrag);
    };
  }, [draggingId, dragOffset]); // eslint-disable-line

  // -------------------------
  // selection, click handlers, actions
  // -------------------------
  const handleNodeClick = (node, e) => {
    e.stopPropagation();
    // toggle selection
    setNodes((prev) => prev.map((n) => (n.id === node.id ? { ...n, state: { ...n.state, selected: !n.state.selected } } : n)));
    setSelectedId(node.id);
    if (node.behavior?.onClick) performAction(node.behavior.onClick, node);
  };

  const performAction = (actionString, node) => {
    if (!actionString) return;
    const [verb, arg] = actionString.split(":");
    switch (verb) {
      case "openPanel":
        setMessage(`Open panel "${arg}" for ${node.id}`);
        break;
      case "showTooltip":
        setMessage(`Tooltip: ${node.semantic?.label || node.id}`);
        break;
      default:
        setMessage(`Unknown action: ${actionString}`);
    }
  };

  // -------------------------
  // Add / Remove objects
  // -------------------------
  const addNode = () => {
    const id = "node-" + Date.now();
    const newNode = {
      id,
      type: "node",
      position: { x: 140 + Math.random() * 200, y: 120 + Math.random() * 120 },
      dimension: { r: 15 },
      ports: [
        { id: `${id}-port-left`, offsetX: -40, offsetY: 0, side: "left" },
        { id: `${id}-port-right`, offsetX: 40, offsetY: 0, side: "right" },
      ],
      semantic: { label: id, type: "entity" },
      style: { color: "#ff66cc" },
      state: { selected: false, active: false },
    };
    setNodes((prev) => {
      const out = [...prev, newNode];
      setTimeout(exportStateToYaml, 0);
      return out;
    });
  };

  const addEdge = () => {
    const id = "edge-" + Date.now();
    const newEdge = {
      id,
      type: "edge",
      geometry: { x1: 0, y1: 0, x2: 0, y2: 0 }, // will be computed in render/export if meta.from/to set
      meta: {}, // expect user to fill meta.from and meta.to via YAML or UI
      style: { color: "#66ff66" },
      state: { selected: false },
    };
    setNodes((prev) => {
      const out = [...prev, newEdge];
      setTimeout(exportStateToYaml, 0);
      return out;
    });
  };

  const addArea = () => {
    const id = "area-" + Date.now();
    const newArea = {
      id,
      type: "area",
      geometry: { x: 200 + Math.random() * 100, y: 200 + Math.random() * 80, width: 160, height: 110 },
      semantic: { label: id, type: "zone" },
      style: { color: "#3399ff33" },
      state: { selected: false, active: false },
    };
    setNodes((prev) => {
      const out = [...prev, newArea];
      setTimeout(exportStateToYaml, 0);
      return out;
    });
  };

  // -------------------------
  // Copy / Paste
  // -------------------------
  const copySelected = () => {
    const sel = nodes.find((n) => n.id === selectedId);
    if (!sel) {
      setMessage("Select a node or area to copy first");
      return;
    }
    setCopiedNode(JSON.parse(JSON.stringify(sel)));
    setAwaitingPaste(true);
    setMessage("Click on canvas to paste the copied item");
  };

  const cancelPaste = () => {
    setAwaitingPaste(false);
    setCopiedNode(null);
    setMessage("");
  };

  // -------------------------
  // Auto layout (step 1: grouped grid by semantic.type)
  // -------------------------
  const autoLayoutFromYaml = (currentNodes, opts = {}) => {
    const paddingX = opts.paddingX ?? 220;
    const paddingY = opts.paddingY ?? 80;
    const startX = opts.startX ?? 120;
    const startY = opts.startY ?? 80;
    const nodeWidth = opts.nodeWidth ?? 120;
    const nodeHeight = opts.nodeHeight ?? 40;

    // only layout 'node' types (areas/edges are left as-is)
    const onlyNodes = currentNodes.filter((n) => n.type === "node");
    const buckets = {};
    onlyNodes.forEach((n) => {
      const t = (n.semantic?.type) || "default";
      if (!buckets[t]) buckets[t] = [];
      buckets[t].push(n);
    });

    const bucketKeys = Object.keys(buckets).sort();
    const placed = new Map();

    const out = currentNodes.map((n) => ({ ...n })); // shallow clone

    bucketKeys.forEach((bk, colIdx) => {
      const list = buckets[bk];
      list.forEach((node, rowIdx) => {
        const x = startX + colIdx * paddingX;
        const y = startY + rowIdx * paddingY;
        // find node in out and set position
        const idx = out.findIndex((o) => o.id === node.id);
        if (idx >= 0) {
          out[idx].position = { x, y };
          out[idx].dimension = out[idx].dimension || { r: 15 };
          // auto-generate 2 ports if none exist
          if (!out[idx].ports || !out[idx].ports.length) {
            out[idx].ports = [
              { id: `${node.id}-port-left`, offsetX: -nodeWidth / 2, offsetY: 0, side: "left" },
              { id: `${node.id}-port-right`, offsetX: nodeWidth / 2, offsetY: 0, side: "right" },
            ];
          }
          placed.set(node.id, true);
        }
      });
    });

    return out;
  };

  // -------------------------
  // Render helpers
  // -------------------------
  const renderGrid = () => {
    const vw = 1600;
    const vh = 1000;
    const lines = [];
    const step = 50;
    for (let x = 0; x <= vw; x += step)
      lines.push(<line key={"gx" + x} x1={x} y1={0} x2={x} y2={vh} stroke="#333" strokeWidth={0.3 / zoom} />);
    for (let y = 0; y <= vh; y += step)
      lines.push(<line key={"gy" + y} x1={0} y1={y} x2={vw} y2={y} stroke="#333" strokeWidth={0.3 / zoom} />);
    return lines;
  };

  // -------------------------
  // UI: buttons + layout triggers
  // -------------------------
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ width: "95%", height: "90%", background: "#0b0b0b", borderRadius: 8, display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: 10, display: "flex", gap: 8, alignItems: "center", background: "#1976d2", color: "#fff" }}>
          <b>Tractum SVG Editor — YAML ↔ SVG</b>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button onClick={() => { setYamlText(SAMPLE_YAML); loadYamlToState(SAMPLE_YAML); }}>Load sample</button>
            <button onClick={() => { const out = exportStateToYaml(); navigator.clipboard?.writeText(out); setMessage("YAML copied to clipboard"); }}>Export YAML (copy)</button>
            <button onClick={() => { if (onClose) onClose(); }}>Close</button>
          </div>
        </div>

        <div style={{ flex: 1, display: "flex" }}>
          {/* Left Panel */}
          <div style={{ width: 160, background: "#111", color: "#fff", padding: 8, display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={addNode}>Add Node</button>
            <button onClick={addEdge}>Add Edge</button>
            <button onClick={addArea}>Add Area</button>
            <hr style={{ borderColor: "#222" }} />
            <button onClick={() => {
              const out = autoLayoutFromYaml(nodes);
              setNodes(out);
              setTimeout(exportStateToYaml, 0);
            }}>Auto Layout</button>
            <button onClick={() => { setZoom((z) => Math.min(4, z * 1.1)); }}>Zoom +</button>
            <button onClick={() => { setZoom((z) => Math.max(0.25, z * 0.9)); }}>Zoom −</button>
            <button onClick={() => setOffset({ x: 0, y: 0 })}>Reset Pan</button>
            <hr style={{ borderColor: "#222" }} />
            <button disabled={!selectedId} onClick={() => {
              const sel = nodes.find((n) => n.id === selectedId);
              if (!sel) return;
              // toggle selected state already done via click, but also allow explicit copy
              setCopiedNode(JSON.parse(JSON.stringify(sel)));
              setAwaitingPaste(true);
              setMessage("Copy ready: click on canvas to paste");
            }}>Copy → Click to Paste</button>
            <button onClick={cancelPaste} disabled={!awaitingPaste}>Cancel Paste</button>
          </div>

          {/* SVG Canvas */}
          <div style={{ flex: 3, position: "relative" }}
            onMouseDown={handleBackgroundMouseDown}
            onMouseMove={handleBackgroundMouseMove}
          >
            <svg ref={svgRef} width="100%" height="100%" style={{ background: "#050505" }}>
              <g transform={`translate(${offset.x},${offset.y}) scale(${zoom})`} data-canvas>
                {/* Grid */}
                {renderGrid()}

                {/* Areas (render under everything) */}
                {nodes.filter((n) => n.type === "area").map((a) => (
                  <g key={a.id} transform={`translate(0,0)`}>
                    <rect
                      x={a.geometry.x}
                      y={a.geometry.y}
                      width={a.geometry.width}
                      height={a.geometry.height}
                      fill={a.style?.color || "#8888"}
                      stroke={a.state?.active ? "#fff" : "transparent"}
                      strokeWidth={a.state?.active ? 2 / zoom : 0}
                      opacity={a.state?.selected ? 0.6 : 0.45}
                      style={{ cursor: "grab" }}
                      onMouseDown={(e) => startDrag(a, e)}
                      onClick={(ev) => handleNodeClick(a, ev)}
                    />
                    <text x={a.geometry.x + 8} y={a.geometry.y + 18} fontSize={12 / zoom} fill="#fff">{a.semantic?.label || a.id}</text>
                  </g>
                ))}

                {/* Edges (lines drawn dynamically between ports/nodes) */}
                {nodes.filter((n) => n.type === "edge").map((edge) => {
                  const geo = computeEdgeGeometry(edge, nodes);
                  return (
                    <line
                      key={edge.id}
                      x1={geo.x1}
                      y1={geo.y1}
                      x2={geo.x2}
                      y2={geo.y2}
                      stroke={edge.style?.color || "#888"}
                      strokeWidth={2 / zoom}
                      opacity={edge.state?.selected ? 0.6 : 1}
                      onClick={(ev) => { ev.stopPropagation(); setSelectedId(edge.id); }}
                    />
                  );
                })}

                {/* Nodes */}
                {nodes.filter((n) => n.type === "node").map((n) => (
                  <g key={n.id} transform={`translate(${n.position.x},${n.position.y})`}>
                    {/* main node circle */}
                    <circle
                      cx={0}
                      cy={0}
                      r={n.dimension?.r || 15}
                      fill={n.style?.color || "#999"}
                      stroke={n.state?.active ? "#fff" : "transparent"}
                      strokeWidth={n.state?.active ? 2 / zoom : 0}
                      opacity={n.state?.selected ? 0.6 : 1}
                      style={{ cursor: draggingId === n.id ? "grabbing" : "grab" }}
                      onMouseDown={(e) => startDrag(n, e)}
                      onClick={(ev) => handleNodeClick(n, ev)}
                    />
                    <text x={(n.dimension?.r || 15) + 8} y={4} fontSize={12 / zoom} fill="#fff">{n.semantic?.label || n.id}</text>

                    {/* ports (small circles) */}
                    {(n.ports || []).map((p) => {
                      const pos = computePortWorldPosition(n, p);
                      // since group is translated to node center, port relative pos = p.offsetX, p.offsetY
                      return (
                        <circle
                          key={p.id}
                          cx={p.offsetX}
                          cy={p.offsetY}
                          r={4}
                          fill="#222"
                          stroke="#fff"
                          strokeWidth={0.5 / zoom}
                          style={{ cursor: "pointer" }}
                          onClick={(ev) => { ev.stopPropagation(); /* could open port menu */ }}
                        />
                      );
                    })}
                  </g>
                ))}

              </g>
            </svg>
          </div>

          {/* Right Panel */}
          <div style={{ width: 360, background: '#111', color: '#fff', padding: 12, overflow: 'auto' }}>
            <h3>YAML</h3>
            <textarea value={yamlText} onChange={(e) => setYamlText(e.target.value)} style={{ width: '100%', height: 220, background: '#071018', color: '#fff' }} />
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button onClick={() => loadYamlToState(yamlText)}>Load YAML → Canvas</button>
              <button onClick={exportStateToYaml}>Canvas → Export YAML</button>
              <button onClick={() => { setYamlText(''); setNodes([]); setMessage('Cleared canvas'); }}>Clear</button>
            </div>

            <h4 style={{ marginTop: 12 }}>Selected</h4>
            {selectedId ? (() => {
              const sel = nodes.find(n => n.id === selectedId);
              if (!sel) return <p>missing</p>;
              return (
                <div>
                  <p><b>{sel.semantic?.label || sel.id}</b></p>
                  <p>type: {sel.type}</p>
                  <p>id: {sel.id}</p>
                  <p>owner: {sel.semantic?.owner || '-'}</p>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button onClick={() => performAction(sel.behavior?.onClick, sel)}>Run onClick</button>
                    <button onClick={() => setNodes(prev => prev.map(n => n.id === sel.id ? { ...n, state: { ...n.state, active: !n.state.active } } : n))}>Toggle active</button>
                    <button onClick={() => {
                      setNodes(prev => prev.filter(n => n.id !== sel.id));
                      setSelectedId(null);
                      setTimeout(exportStateToYaml, 0);
                    }}>Delete</button>
                    <button onClick={copySelected}>Copy (click-to-paste)</button>
                  </div>

                  {sel.type === "node" && (
                    <>
                      <hr style={{ borderColor: "#222", marginTop: 10 }} />
                      <div>
                        <label>Label</label>
                        <input value={sel.semantic?.label || ""} onChange={(e) => {
                          const v = e.target.value;
                          setNodes(prev => prev.map(n => n.id === sel.id ? { ...n, semantic: { ...n.semantic, label: v } } : n));
                        }} style={{ width: "100%", marginTop: 6 }} />
                        <label style={{ marginTop: 8 }}>Color</label>
                        <input type="color" value={sel.style?.color || "#ffffff"} onChange={(e) => {
                          const v = e.target.value;
                          setNodes(prev => prev.map(n => n.id === sel.id ? { ...n, style: { ...n.style, color: v } } : n));
                        }} />
                        <div style={{ marginTop: 8 }}>
                          <button onClick={() => setTimeout(exportStateToYaml, 0)}>Save Changes to YAML</button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })() : <p>None</p>}

            <h4 style={{ marginTop: 12 }}>Messages</h4>
            <div style={{ minHeight: 80, background: '#071018', padding: 8 }}>{message}</div>
            <div style={{ marginTop: 12 }}>
              <small style={{ color: '#888' }}>Notes: edges will connect to node ports when meta.from/meta.to are set. Use Auto Layout to generate positions & ports.</small>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
